import { LayoutCtx, MenuParamsType } from '@/contexts/Layout/LayoutCtx';
import {
  Button,
  ButtonIcon,
  Footer,
  Header,
  Typography,
} from '@data-products-and-ai/react-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Layout.module.scss';
import { Backdrop, Box, Fade, Modal } from '@mui/material';
import { useAccessToken } from '@/hooks';
import { jwtDecode } from 'jwt-decode';
interface LayoutProps {
  menu: React.ReactNode;
  content: React.ReactNode;
  contentMenu: React.ReactNode;
  contentMenuShow: boolean;
  menuCollapsed: boolean;
  menuWidth: string;
}

/**
 * Layout Component
 * Manages the layout parameters for the menu, content, header, and footer.
 * Uses context to provide layout parameters and update functions to its children.
 * @param {LayoutProps} props - The properties for the layout component
 */
const Layout = (props: LayoutProps) => {
  const [menuParams, setMenuParams] = useState<MenuParamsType>({
    height: '100vh',
    width: props.menuWidth,
    defaultWidth: props.menuWidth,
    collapsedWidth: '100px',
    top: '0px',
    isCollapsed: false,
  });

  const [contentParams, setContentParams] = useState({
    height: '100vh',
    width: '100%',
    top: '0px',
    left: props.menuWidth,
  });
  const [contentMenuParams, setContentMenuParams] = useState({
    height: '0px',
    width: '100%',
    top: '0px',
    left: props.menuWidth,
  });

  const [headerParams, setHeaderParams] = useState({
    height: '0px',
    width: '100%',
    top: '0px',
  });
  const [footerParams, setFooterParams] = useState({
    height: '0px',
    width: '100%',
    bottom: '0px',
  });

  const [openModal, setOpenModal] = useState(false);
  const [showReportButton, setShowReportButton] = useState<boolean>(false);

  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const contentMenuRef = useRef<HTMLDivElement>(null);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    height: '100%',
    maxWidth: '100%',
    bgcolor: 'background.paper',
    border: '0.5px solid #000',
    boxShadow: 24,
    borderRadius: '8px',
    padding: '20px',
  };
  const { getSilentToken } = useAccessToken();

  useEffect(() => {
    async function getToken() {
      const token = await getSilentToken();
      type DecodedTokenWithGroups = {
        [key: string]: unknown;
        groups?: string[];
      };
      const decodedToken = jwtDecode<DecodedTokenWithGroups>(token);
      if (decodedToken.groups) {
        const allowedGroups = new Set([
          'e32f9ff0-6513-444d-b558-43ba05bf7905',
          'cd45212e-0bbf-41ca-b801-a5ce602daa85',
          '6ed8127d-c4b7-416f-ac02-f4d0121f0892',
        ]);
        if (decodedToken.groups.some((group) => allowedGroups.has(group))) {
          setShowReportButton(true);
        } else {
          setShowReportButton(false);
        }
      }
    }
    getToken();
  }, [getSilentToken]);

  useEffect(() => {
    setHeaderParams((prevState) => ({
      ...prevState,
      height: headerRef.current ? `${headerRef.current.offsetHeight}px` : '0px',
    }));
    setFooterParams((prevState) => ({
      ...prevState,
      height: footerRef.current ? `${footerRef.current.offsetHeight}px` : '0px',
    }));
  }, []);

  useEffect(() => {
    setMenuParams((prevState) => ({
      ...prevState,
      height: `calc(100vh - ${headerParams.height} - ${footerParams.height})`,
      top: headerParams.height,
    }));

    setContentMenuParams((prevState) => ({
      ...prevState,
      width: `calc(100% - ${menuParams.width})`,
      top: headerParams.height,
      height: props.contentMenuShow ? '61px' : '0px',
    }));

    console.log('contentMenuShow ran', props.contentMenuShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerParams, footerParams, props.contentMenuShow]);

  useEffect(() => {
    setContentParams((prevState) => ({
      ...prevState,
      width: `calc(100% - ${menuParams.width})`,
      left: menuParams.width,
      top: `calc(${contentMenuParams.height} + ${menuParams.top})`,
      height: `calc(${menuParams.height} - ${contentMenuParams.height}`,
    }));
    console.log(contentMenuParams);
  }, [menuParams, contentMenuParams]);

  const handleModalOpen = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  return (
    <LayoutCtx.Provider
      value={{
        menuParams,
        setMenuParams,
        contentParams,
        setContentParams,
        contentMenuParams,
        setContentMenuParams,
        headerParams,
        setHeaderParams,
        footerParams,
        setFooterParams,
      }}
    >
      <div className={styles.header} ref={headerRef}>
        {/* <Header /> */}
        <Header
          hasSignOut
          viewTicketsURL="https://apps.powerapps.com/play/e/default-254ba93e-1f6f-48f3-90e6-e2766664b477/a/85007077-c155-4ea5-a303-eb1bde994fb7?hidenavbar=true&Lookup=13"
          menuSupportURL="https://apps.powerapps.com/play/e/default-254ba93e-1f6f-48f3-90e6-e2766664b477/a/65af7a77-056b-42bc-9bd4-2de0052b68ac?hidenavbar=true&Lookup=13"
        />
      </div>

      <div
        className={styles.menu}
        style={{
          top: menuParams.top,

          height: menuParams.height,

          width: menuParams.width,
        }}
      >
        {props.menu}
        {showReportButton && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              bottom: '20px',
              left: '100px',
            }}
          >
            <Button
              key="report"
              type="link"
              onClick={handleModalOpen}
              allCaps={false}
            >
              Visual Factory Report
            </Button>
          </div>
        )}
      </div>

      <div
        ref={contentMenuRef}
        className={styles.contentMenu}
        style={{
          top: contentMenuParams.top,
          height: contentMenuParams.height,
          left: contentMenuParams.left,
          width: contentMenuParams.width,
        }}
      >
        <div>{props.contentMenuShow ? props.contentMenu : null}</div>
      </div>

      <div
        className={styles.content}
        style={{
          top: contentParams.top,
          left: contentParams.left,
          height: contentParams.height,

          width: contentParams.width,
        }}
      >
        {props.content}
      </div>

      <Modal
        open={openModal}
        onClose={handleModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openModal}>
          <Box sx={modalStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              <Typography tag="h2">Visual Factory Report</Typography>
              <ButtonIcon
                buttonType="button"
                icon="IconClose"
                onClick={handleModalClose}
              />
            </div>
            <Box sx={{ padding: 0 }}>
              <Box className="iframe-container" sx={{ height: '90vh' }}>
                <iframe
                  title="Visual Factory Report"
                  style={{
                    height: '100%',
                    width: '100%',
                    transform: 'translateX(0%)',
                  }}
                  src={
                    'https://app.powerbi.com/reportEmbed?reportId=784ebf24-fa3c-4d2d-aeb0-46e837661680&autoAuth=true&disableSensitivityBanner=true&ctid=254ba93e-1f6f-48f3-90e6-e2766664b477&pageName=f4f50994a95a7d0ae5a7&filterPaneEnabled=false&navContentPaneEnabled=true'
                  }
                  frameBorder="0"
                ></iframe>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <div ref={footerRef} className={styles.footer}>
        <Footer
          appName="Factory Digital Twin Simulation"
          appVersion={'2.0.3 ' + import.meta.env.VITE_ENVIRONMENT}
          showRestricted={true}
        />
      </div>
    </LayoutCtx.Provider>
  );
};

export default Layout;
